import { useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import Fab from "@mui/material/Fab";
import AddCommentRoundedIcon from "@mui/icons-material/AddCommentRounded";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { useAuth } from "../../context/AuthContext";
import { useGetUserWebAiChatsQuery } from "../../graphql/queries/getUserWebAiChats.generated";
import { useGetNonAdminAiChatsQuery } from "../../graphql/queries/getNonAdminAiChats.generated";
import { useGetUserQuery } from "../../graphql/queries/getUser.generated";
import Colors from "../../styles/colors";
import routeNames from "../../constants/route-names";
import dayjs from "dayjs";
import { ApolloError } from "@apollo/client";

type NonAdminMessage = {
  __typename?: "messages";
  senderId?: string | null;
  aiChatId?: string | null;
  id: any;
  user?: {
    __typename?: "users";
    firstName: string;
    id: string;
    lastName: string;
    profilePhotoLink?: string | null;
  } | null;
  workspaceId?: number | null;
  workspace?: {
    __typename?: "workspaces";
    id: number;
    name: string;
  } | null;
  createdAt: string;
  body?: string | null;
};

const AiChats = () => {
  const { loggedUser } = useAuth();
  const navigate = useNavigate();

  const handleOnErrorsGetUsers = useCallback((error: ApolloError) => {
    console.log("Get Users Error: ", error);
  }, []);

  const { data, loading, refetch } = useGetUserQuery({
    skip: !loggedUser?.uid,
    variables: {
      id: loggedUser?.uid,
    },
    errorPolicy: "none",
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorsGetUsers,
  });

  useEffect(() => {
    const handleFocus = () => {
      if (data?.users_by_pk) {
        if (
          data.users_by_pk.isHighnoteAdmin ||
          data.users_by_pk.isWorkspaceAdmin
        ) {
          refetchAdminChats();
        } else {
          refetchNonAdminChats();
        }
      }
    };
    window.addEventListener("focus", handleFocus);

    handleFocus();

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [data?.users_by_pk]);

  const navigateToNewChat = useCallback(() => {
    if (!data?.users_by_pk) return;
    navigate(
      data.users_by_pk.isHighnoteAdmin || data?.users_by_pk?.isWorkspaceAdmin
        ? routeNames.AI_CHATS_NEW
        : routeNames.AI_CHATS_NEW_NONADMIN
    );
  }, [data?.users_by_pk?.isHighnoteAdmin, data?.users_by_pk?.isWorkspaceAdmin]);

  const navigateToExistingChat = useCallback(
    (chatId: string, messageId?: string) => {
      if (!data?.users_by_pk) return;

      const basePath =
        data.users_by_pk.isHighnoteAdmin || data?.users_by_pk?.isWorkspaceAdmin
          ? routeNames.AI_CHATS
          : routeNames.AI_CHATS_NONADMIN;

      const path = messageId
        ? `${basePath}/${chatId}/${messageId}`
        : `${basePath}/${chatId}`;

      navigate(path);
    },
    [
      data?.users_by_pk?.isHighnoteAdmin,
      data?.users_by_pk?.isWorkspaceAdmin,
      navigate,
    ]
  );

  const {
    data: adminChats,
    loading: loadingAdmin,
    refetch: refetchAdminChats,
  } = useGetUserWebAiChatsQuery({
    skip: !data?.users_by_pk || !loggedUser?.uid,
    variables: { loggedUserId: loggedUser?.uid || "" },
  });

  const {
    data: nonAdminChats,
    loading: loadingNonAdmin,
    refetch: refetchNonAdminChats,
  } = useGetNonAdminAiChatsQuery({
    skip: !data?.users_by_pk || !loggedUser?.uid,
    variables: { userId: loggedUser?.uid || "" },
  });

  const adminChatsList = useMemo(() => {
    if (loadingAdmin) return <p>Loading...</p>;
    if (!adminChats?.webAiChats?.length) return <p>No admin chats found</p>;

    return (
      <List sx={{ width: "100%", bgcolor: "background.paper" }}>
        {adminChats.webAiChats.map((chat) => (
          <ListItem
            key={`admin-chat-${chat.id}`}
            alignItems="flex-start"
            divider
            sx={{
              "&.MuiListItem-root": {
                borderColor: Colors.Highnote.blue4T90,
                borderBottomWidth: 1,
              },
            }}
          >
            <ListItemButton
              onClick={() => navigateToExistingChat(chat.aiChatId)}
            >
              <ListItemAvatar>
                <Avatar
                  alt={`${chat.user?.firstName || ""} ${
                    chat.user?.lastName || ""
                  }`}
                  src={chat.user?.profilePhotoLink || undefined}
                />
              </ListItemAvatar>
              <ListItemText
                primary={chat.starterMessage || "No message"}
                secondary={
                  <>
                    {chat.workspace?.name && (
                      <Typography
                        sx={{ display: "inline" }}
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {chat.workspace.name}
                      </Typography>
                    )}
                    {`${chat.workspace?.name ? " — " : ""}${dayjs(
                      chat.createdAt
                    ).format("MM-DD-YYYY • HH:mm")}`}
                  </>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  }, [adminChats, loadingAdmin, navigateToExistingChat]);

  const nonAdminChatsList = useMemo(() => {
    if (loadingNonAdmin) return <p>Loading...</p>;
    if (!nonAdminChats?.messages?.length) return <p>No chats found</p>;

    return (
      <List sx={{ width: "100%", bgcolor: "background.paper" }}>
        {nonAdminChats.messages.map((message: NonAdminMessage) => (
          <ListItem
            key={`nonadmin-chat-${message.id}`}
            alignItems="flex-start"
            divider
            sx={{
              "&.MuiListItem-root": {
                borderColor: Colors.Highnote.blue4T90,
                borderBottomWidth: 1,
              },
            }}
          >
            <ListItemButton
              onClick={() =>
                message.aiChatId &&
                navigateToExistingChat(message.aiChatId, message.id)
              }
            >
              <ListItemAvatar>
                <Avatar
                  alt={`${message.user?.firstName || ""} ${
                    message.user?.lastName || ""
                  }`}
                  src={message.user?.profilePhotoLink || undefined}
                />
              </ListItemAvatar>
              <ListItemText
                primary={message.body || "No message"}
                secondary={
                  <>
                    {message.workspace?.name && (
                      <Typography
                        sx={{ display: "inline" }}
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {message.workspace.name}
                      </Typography>
                    )}
                    {`${message.workspace?.name ? " — " : ""}${dayjs(
                      message.createdAt
                    ).format("MM-DD-YYYY • HH:mm")}`}
                  </>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  }, [nonAdminChats, loadingNonAdmin, navigateToExistingChat]);

  return (
    <Container component="main" maxWidth={false}>
      {/* <h1 className="screen-title">
        {data?.users_by_pk?.isHighnoteAdmin ||
        data?.users_by_pk?.isWorkspaceAdmin
          ? "Talk to your data"
          : "Chat with HiGnome"}
      </h1> */}

      <h1 className="screen-title" style={{ marginBottom: "24px" }}>
        HiGnome AI Assistant
      </h1>
      <Fab
        variant="extended"
        color="primary"
        sx={{ backgroundColor: Colors.Highnote.blue3, mb: 3 }}
        onClick={navigateToNewChat}
      >
        <AddCommentRoundedIcon sx={{ mr: 1 }} />
        New chat
      </Fab>

      <h3>Chat History</h3>
      {data?.users_by_pk ? (
        data.users_by_pk.isHighnoteAdmin ||
        data?.users_by_pk?.isWorkspaceAdmin ? (
          <div>{adminChatsList}</div>
        ) : (
          <div>{nonAdminChatsList}</div>
        )
      ) : null}
    </Container>
  );
};

export default AiChats;
